import { Action as SyncEngineAction } from "@workers/rest-api";
import { clone, isEmpty, keyBy } from "lodash";
import { getSnapshot, Instance, SnapshotIn, types } from "mobx-state-tree";
import { $nonEmptyObject } from "mobx-state-tree/dist/internal";

import { getTiptapText } from "~/components/SaveForm/AddNote";
import { backlogSlug } from "~/app/(app)/(authenticated)/[account]/(account)/backlog";

type ModelCreateParams<T> = Omit<SnapshotIn<T>, typeof $nonEmptyObject>;
type ModelUpdateParams<T> = Partial<
  Omit<ModelCreateParams<T>, "id" | "createdAt">
>;

// ----------------
// Collection
//
const CollectionModel = types.model("CollectionModel", {
  id: types.identifier,
  slug: types.string,
  title: types.string,
  description: types.maybeNull(types.string),
});

export type Collection = Instance<typeof CollectionModel>;
export type CollectionCreateParams = ModelCreateParams<Collection>;
export type CollectionUpdateParams = ModelUpdateParams<Collection>;
export type CollectionSnapshot = SnapshotIn<Collection>;

// ----------------
// Tag
//
const TagModel = types.model("TagModel", {
  id: types.identifier,
  value: types.string,
});

export type Tag = Instance<typeof TagModel>;
export type TagCreateParams = ModelCreateParams<Tag>;
export type TagUpdateParams = ModelUpdateParams<Tag>;
export type TagSnapshot = SnapshotIn<Tag>;

// ----------------
// Save
//
const _SaveBaseModel = types
  .model("_SaveBaseModel", {
    id: types.identifier,
    title: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
    createdAt: types.optional(types.Date, () => new Date()),
    updatedAt: types.maybeNull(types.Date),
    tags: types.optional(
      types.map(types.safeReference(TagModel, { acceptsUndefined: false })),
      {},
    ),
    collections: types.optional(
      types.map(
        types.safeReference(CollectionModel, { acceptsUndefined: false }),
      ),
      {},
    ),
  })
  .views((self) => ({
    collectionsAsArray() {
      return Array.from(self.collections.values()).toSorted((a, b) =>
        a.title.localeCompare(b.title),
      );
    },
    tagsAsArray() {
      return Array.from(self.tags.values()).toSorted((a, b) =>
        a.value.localeCompare(b.value),
      );
    },
  }));

const SaveUrlModel = types.compose(
  "SaveUrlModel",
  _SaveBaseModel,
  types.model({
    type: types.literal("URL"),
    url: types.string,
    meta: types.maybeNull(
      types.model({
        title: types.optional(types.string, ""),
        description: types.optional(types.string, ""),
        image: types.optional(types.string, ""),
        logo: types.optional(types.string, ""),
      }),
    ),
  }),
);
export type SaveUrl = Instance<typeof SaveUrlModel>;
export type SaveUrlCreateParams = Omit<ModelCreateParams<SaveUrl>, "type">;
export type SaveUrlUpdateParams = Omit<ModelUpdateParams<SaveUrl>, "type">;

const SaveMediaModel = types.compose(
  "SaveMediaModel",
  _SaveBaseModel,
  types.model({
    type: types.literal("MEDIA"),
    r2Key: types.string,
    meta: types.maybeNull(
      types.model("SaveMediaModel", {
        assetId: types.optional(types.string, ""),
        width: types.optional(types.number, 0),
        height: types.optional(types.number, 0),
        type: types.enumeration(["video", "image"]),
        fileName: types.optional(types.string, ""),
        fileSize: types.optional(types.number, 0),
        duration: types.optional(types.number, 0),
        mimeType: types.optional(types.string, ""),
      }),
    ),
  }),
);

export type SaveMedia = Instance<typeof SaveMediaModel>;

const SaveFileModel = types.compose(
  "SaveFileModel",
  _SaveBaseModel,
  types.model("SaveFileModel", {
    type: types.literal("FILE"),
    r2Key: types.string,
    meta: types.maybeNull(
      types.model({
        name: types.string,
        size: types.optional(types.number, 0),
        mimeType: types.optional(types.string, ""),
      }),
    ),
  }),
);

export type SaveFile = Instance<typeof SaveFileModel>;

const SaveNoteModel = types.compose(
  "SaveNoteModel",
  _SaveBaseModel,
  types
    .model("SaveNoteModel", {
      type: types.literal("NOTE"),
      // TODO: Can this be JSON?
      richTextAST: types.string,
      // meta: types.maybeNull(
      //   types.model({
      //     name: types.string,
      //     size: types.optional(types.number, 0),
      //     mimeType: types.optional(types.string, ""),
      //   }),
      // ),
    })
    .views((self) => ({
      get richTextAsText() {
        try {
          return getTiptapText(JSON.parse(self.richTextAST));
        } catch {
          return "";
        }
      },
    })),
);

export type SaveNote = Instance<typeof SaveNoteModel>;

const SaveModel = types.union(
  {
    dispatcher: (snapshot) => {
      if (snapshot.type === "FILE") {
        return SaveFileModel;
      }
      if (snapshot.type === "MEDIA") {
        return SaveMediaModel;
      }
      if (snapshot.type === "NOTE") {
        return SaveNoteModel;
      }

      return SaveUrlModel;
    },
    eager: false,
  },
  SaveUrlModel,
  SaveMediaModel,
  SaveFileModel,
  SaveNoteModel,
);
export type Save = Instance<typeof SaveModel>;
export type SaveSnapshot = SnapshotIn<Save>;

// ----------------
// Space
//
const SpaceModel = types
  .model("SpaceModel", {
    id: types.identifier,
    type: types.union(types.literal("SYSTEM"), types.literal("CUSTOM")),
    slug: types.string,
    displayName: types.maybeNull(types.string),
    r2Key: types.maybeNull(types.string),
    description: types.maybeNull(types.string),

    // relations
    saves: types.optional(types.map(SaveModel), {}),
    collections: types.optional(types.map(CollectionModel), {}),
    tags: types.optional(types.map(TagModel), {}),
  })
  .views((self) => ({
    get savesAsArray() {
      return Array.from(self.saves.values());
    },
    get savesCount() {
      return self.saves.size;
    },
    saveById(id: string) {
      return self.saves.get(id);
    },
    get collectionsAsArray() {
      return Array.from(self.collections.values()).toSorted((a, b) =>
        a.title.localeCompare(b.title),
      );
    },
    get collectionsCount() {
      return self.collections.size;
    },
    collectionById(id: string) {
      return self.collections.get(id);
    },
    collectionBySlug(slug: string) {
      return Array.from(self.collections.values()).find(
        (collection) => collection.slug === slug,
      );
    },
    get tagsAsArray() {
      return Array.from(self.tags.values()).toSorted((a, b) =>
        a.value.localeCompare(b.value),
      );
    },
    get tagsCount() {
      return self.tags.size;
    },
    tagById(id: string) {
      return self.tags.get(id);
    },
    tagByValue(value: string) {
      return Array.from(self.tags.values()).find((tag) => tag.value === value);
    },
  }));

export type Space = Instance<typeof SpaceModel>;
export type SpaceCreateParams = Omit<
  SnapshotIn<Space>,
  "type" | "collections" | "saves" | "tags" | typeof $nonEmptyObject
>;
export type SpaceUpdateParams = Partial<Omit<SpaceCreateParams, "id">>;
export type SpaceSnapshot = SnapshotIn<Space>;

// ----------------
// Account
//

// Duplicate Account to avoid circular dependency. This is for display only so should not be a problem
const MemberAccountModel = types.model("MemberAccountModel", {
  id: types.identifier,
  slug: types.string,
  displayName: types.maybeNull(types.string),
  avatarUrl: types.maybeNull(types.string),
});

const MemberModel = types.model("MemberModel", {
  id: types.identifier,
  role: types.union(
    types.literal("OWNER"),
    types.literal("MEMBER"),
    types.literal("GUEST"),
  ),
  userAccount: MemberAccountModel,
});

export type Member = Instance<typeof MemberModel>;
export type MemberSnapshot = SnapshotIn<typeof MemberModel>;

const InviteModel = types.model("InviteModel", {
  id: types.identifier,
  email: types.string,
  createdAt: types.Date,
  role: types.union(
    types.literal("OWNER"),
    types.literal("MEMBER"),
    types.literal("GUEST"),
  ),
});

export type Invite = Instance<typeof InviteModel>;
export type InviteSnapshot = SnapshotIn<typeof InviteModel>;

const AccountModelBase = types
  .model("AccountModel", {
    id: types.identifier,
    type: types.union(types.literal("USER"), types.literal("ORGANIZATION")),
    slug: types.string,
    displayName: types.maybeNull(types.string),
    bio: types.maybeNull(types.string),
    avatarUrl: types.maybeNull(types.string),

    // relations
    members: types.optional(types.map(MemberModel), {}),
    invites: types.optional(types.map(InviteModel), {}),
    spaces: types.map(SpaceModel),
  })
  .views((self) => ({
    get spacesAsArray() {
      return Array.from(self.spaces.values()).toSorted((a, b) =>
        a.displayName && b.displayName
          ? a.displayName.localeCompare(b?.displayName)
          : 0,
      );
    },
    get spacesAsArrayWithoutBacklog() {
      return Array.from(self.spaces.values())
        .filter((s) => s.slug !== backlogSlug)
        .toSorted((a, b) =>
          a.displayName && b.displayName
            ? a.displayName.localeCompare(b?.displayName)
            : 0,
        );
    },
    get membersAsArray() {
      return Array.from(self.members.values());
    },
    get invitesAsArray() {
      return Array.from(self.invites.values());
    },
    spaceBySlug(slug: string) {
      return Array.from(self.spaces.values()).find(
        (space) => space.slug === slug,
      );
    },
    spaceById(id: string) {
      return self.spaces.get(id);
    },
  }));

export const AccountModel = AccountModelBase.actions(accountActions);

type SyncEngineActionCreators = {
  // [T in SyncEngineAction as T["type"]]: (params: T["payload"]) => any;
  // we're using `any` here cuz `payload` types from BE clashes with the ones we define, and with `any`, `satifies` does the correct type narrowing
  [T in SyncEngineAction["type"]]: (
    params: Extract<SyncEngineAction, { type: T }>["payload"],
  ) => any;
};

function accountActions(self: Instance<typeof AccountModelBase>) {
  const actions = {
    /**
     * DO NOT USE THESE DIRECTLY
     * used only to get server updates
     */
    updateAccount(params) {
      const { set } = params;
      if (set.avatarUrl !== undefined) self.avatarUrl = set.avatarUrl;
      if (set.displayName !== undefined) self.displayName = set.displayName;
      if (set.slug !== undefined) self.slug = set.slug;
    },
    addInvite(params) {
      const { set } = params;
      const d = new Date(set.createdAt);
      self.invites.set(set.id, InviteModel.create({ ...set, createdAt: d }));
    },
    deleteInvite(params) {
      self.invites.delete(params.inviteId);
    },
    acceptInvite(params) {
      const { inviteId, set } = params;
      const { member } = set;
      self.members.set(member.id, MemberModel.create(member));
      self.invites.delete(inviteId);
    },
    addMember(params) {
      const { set } = params;
      self.members.set(set.id, MemberModel.create(set));
    },
    deleteMember(params) {
      self.members.delete(params.memberId);
    },
    updateMember(params) {
      const { memberId, set } = params;
      const member = self.members.get(memberId);

      if (member) {
        const { role, userAccount } = set;
        if (role) member.role = role;
        if (userAccount) applyUpdates(member.userAccount, userAccount);
      }
    },
    // ----------------
    //#region Space actions
    //
    createSpace(params) {
      const { set } = params;
      const updatedSet = {
        ...set,
        r2Key: set.r2Key?.replace("temp/", ""),
      };
      self.spaces.set(
        set.id,
        SpaceModel.create({ ...updatedSet, type: "CUSTOM" }),
      );
    },
    updateSpace(params) {
      const { spaceId, set } = params;
      const space = self.spaceById(spaceId);
      const updatedSet = {
        ...set,
        r2Key: set.r2Key?.replace("temp/", ""),
      };
      if (space) {
        applyUpdates(space, updatedSet);
      }
    },
    deleteSpace(params) {
      self.spaces.delete(params.spaceId);
    },
    //#endregion
    // ----------------
    //#region  Collection actions
    //
    createCollection(params: { spaceId: string; set: CollectionCreateParams }) {
      const { spaceId, set } = params;
      const space = self.spaceById(spaceId);
      if (space) {
        if (space.slug === backlogSlug) {
          console.warn("Can't create collection in backlog");
          return;
        }

        space.collections.set(set.id, CollectionModel.create(set));
      }
    },
    updateCollection(params: {
      spaceId: string;
      collectionId: string;
      set: CollectionUpdateParams;
    }) {
      const { spaceId, collectionId, set } = params;
      const space = self.spaceById(spaceId);
      if (space) {
        const collection = space.collections.get(collectionId);
        if (collection) {
          applyUpdates(collection, set);
        }
      }
    },
    deleteCollection(params: { spaceId: string; collectionId: string }) {
      const { spaceId, collectionId } = params;
      const space = self.spaceById(spaceId);
      if (space) {
        space.collections.delete(collectionId);
      }
    },
    //#endregion
    // ----------------
    //#region Tag actions
    //
    createTag(params) {
      const { spaceId, set } = params;
      const space = self.spaceById(spaceId);
      if (space) {
        space.tags.set(set.id, TagModel.create(set));
      }
    },
    updateTag(params) {
      const { spaceId, tagId, set } = params;
      const space = self.spaceById(spaceId);
      if (space) {
        const tag = space.tags.get(tagId);
        if (tag) {
          applyUpdates(tag, set);
        }
      }
    },
    deleteTag(params) {
      const { spaceId, tagId } = params;
      const space = self.spaceById(spaceId);
      if (space) {
        space.tags.delete(tagId);
      }
    },
    //#endregion
    // ----------------
    //#region Save actions
    //
    createSave(params) {
      const { spaceId } = params;
      let { set } = params;
      const space = self.spaceById(spaceId);

      // doing this in SaveModel.create gives TS error 🤷🏻
      if (set.type === "FILE" || set.type === "MEDIA") {
        set = {
          ...set,
          r2Key: set.r2Key.replace("temp/", ""),
        };
      }

      if (space) {
        space.saves.set(
          set.id,

          SaveModel.create({
            ...set,

            collections: keyBy(set.collections ?? []),
            tags: keyBy(set.tags ?? []),
            // @ts-expect-error in mobx AST is string.
            // API gives JSON, we send JSON, but store it as string
            richTextAST:
              set.type === "NOTE"
                ? JSON.stringify(set.richTextAST)
                : JSON.stringify(""),
          }),
        );

        if (set.collections) {
          set.collections.forEach((collectionId) => {
            const collection = space.collections.get(collectionId);
            const save = space.saves.get(set.id);

            if (save && collection) {
              save.collections.set(collection.id, collection);
            }
          });
        }

        if (set.tags) {
          set.tags.forEach((tagId) => {
            const tag = space.tags.get(tagId);
            const save = space.saves.get(set.id);

            if (save && tag) {
              save.tags.set(tag.id, tag);
            }
          });
        }
      }
    },
    deleteSave(params) {
      const { spaceId, saveId } = params;
      const space = self.spaceById(spaceId);
      if (space) {
        space.saves.delete(saveId);
      }
    },
    updateSave(params) {
      const { spaceId, saveId, set: _set } = params;
      let set = clone(_set);
      const sourceSpace = self.spaceById(spaceId);
      if (!sourceSpace) return;

      const sourceSave = sourceSpace?.saveById(saveId);
      if (!sourceSave) return;

      if (set.type === "NOTE") {
        set = {
          ...set,
          // @ts-expect-error in mobx AST is string.
          // API gives JSON, we send JSON, but store it as string
          richTextAST: JSON.stringify(set.richTextAST),
        };
      }
      // if user tries to change the space
      if (set.spaceId && set.spaceId !== spaceId) {
        const targetSpace = self.spaceById(set.spaceId);
        if (!targetSpace) return;

        // const newID =
        // id: generateObjectId(),

        targetSpace.saves.put({
          ...getSnapshot(sourceSave),
        });

        sourceSpace.saves.delete(saveId);

        const targetSave = targetSpace.saveById(saveId);
        if (!targetSave) return;

        if (!isEmpty(set.addToCollections)) {
          set.addToCollections?.forEach((collectionId) => {
            const collection = targetSpace.collections.get(collectionId);
            if (!collection) return;

            targetSave.collections.set(collection.id, collection);
          });
        }

        if (!isEmpty(set.tagsToAdd)) {
          set.tagsToAdd?.forEach((tagId) => {
            const tag = targetSpace.tags.get(tagId);
            if (!tag) return;

            targetSave.tags.set(tag.id, tag);
          });
        }

        return;
      }

      applyUpdates(sourceSave, set);

      if (!isEmpty(set.addToCollections)) {
        set.addToCollections?.forEach((collectionId) => {
          const collection = sourceSpace.collections.get(collectionId);
          const save = sourceSpace.saves.get(saveId);

          if (save && collection) {
            save.collections.set(collection.id, collection);
          }
        });
      }

      if (!isEmpty(set.removeFromCollections)) {
        set.removeFromCollections?.forEach((collectionId) => {
          const collection = sourceSpace.collections.get(collectionId);
          const save = sourceSpace.saves.get(saveId);

          if (save && collection) {
            save.collections.delete(collection.id);
          }
        });
      }

      if (!isEmpty(set.tagsToAdd)) {
        set.tagsToAdd?.forEach((tagId) => {
          const tag = sourceSpace.tags.get(tagId);
          const save = sourceSpace.saves.get(saveId);

          if (save && tag) {
            save.tags.set(tag.id, tag);
          }
        });
      }

      if (!isEmpty(set.tagsToRemove)) {
        set.tagsToRemove?.forEach((tagId) => {
          const tag = sourceSpace.tags.get(tagId);
          const save = sourceSpace.saves.get(saveId);

          if (save && tag) {
            save.tags.delete(tag.id);
          }
        });
      }
    },
  } satisfies SyncEngineActionCreators;
  return actions;
}
export type AccountType = Instance<typeof AccountModel>;
export type AccountSnapshot = SnapshotIn<typeof AccountModel>;

export type AccountActions = ReturnType<typeof accountActions>;

function applyUpdates<T, U extends ModelUpdateParams<T>>(
  target: T,
  updates: U,
) {
  Object.keys(updates).forEach((key) => {
    const value = updates[key as keyof U];
    if (typeof value !== "undefined") {
      // @ts-expect-error TODO fix this
      target[key] = value;
    }
  });
}
